import type { DatePickerLocalization } from "@edaz/edazkit/DatePicker";
import type { UploaderLocalization } from "@edaz/edazkit/Uploader";
import type { DateRangeInputLocalization } from "@edaz/edazkit/DateRangeInput";
import type { InternalEnumNames } from "services/localizationService";
import type LocalizationService from "services/localizationService";
import type EnumService from "services/enumService";
import type { EntityNames } from "../config";
import type { NumberRangeInputLocalization } from "@edaz/edazkit/NumberRangeInput";
import type LicensePlate from "../entities/licensePlate";
import type Vehicle from "../entities/vehicleResponse";

class LocalizationProvider {
  private localizationService: LocalizationService;
  private enumService: EnumService;

  initialize(localizationService: LocalizationService, enumService: EnumService) {
    this.localizationService = localizationService;
    this.enumService = enumService;
  }

  formatDate(date?: Date | null, options?: Intl.DateTimeFormatOptions) {
    return this.localizationService.formatDate(date, options);
  }

  formatDateTime(date?: Date | null, options?: Intl.DateTimeFormatOptions) {
    return this.localizationService.formatDateTime(date, options);
  }

  formatTime(date?: Date | null, options?: Intl.DateTimeFormatOptions) {
    return this.localizationService.formatTime(date, options);
  }

  formatNumber(num: number, options?: Intl.NumberFormatOptions) {
    return this.localizationService.formatNumber(num, options);
  }

  formatLicensePlate(vehicle: LicensePlate | Vehicle) {
    const countryCode = this.enumService.value("countries", vehicle.countryId)?.code ?? "";
    return `${countryCode} – ${vehicle.licensePlate}`;
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateModel(model: EntityNames, size?: number) {
    return this.localizationService.translateModel(model, size);
  }

  translateEnum(enumName: InternalEnumNames, value: any, size?: number) {
    return this.localizationService.translateEnum(enumName as any, value, size);
  }

  get uploaderLocalization(): UploaderLocalization {
    return {
      drag: this.localizationService.translateGeneral("uploader.drag"),
      or: this.localizationService.translateGeneral("uploader.or"),
      select: this.localizationService.translateGeneral("uploader.select"),
    };
  }

  get datePickerLocalization(): DatePickerLocalization {
    const days = ["su", "mo", "tu", "we", "th", "fr", "sa"];
    const getMonth = (month: string) => this.localizationService.translateGeneral(`datepicker.strings.months.${month}`);
    const getDays = (length: string) =>
      days.map(day => this.localizationService.translateGeneral(`datepicker.strings.weekdays${length}.${day}`));

    return {
      format: this.localizationService.translateGeneral("datepicker.format"),
      strings: {
        months: [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ].map(getMonth),
        weekdaysLong: getDays("Long"),
        weekdaysShort: getDays("Short"),
        firstDayOfWeek: days.indexOf(this.localizationService.translateGeneral("datepicker.firstDayOfWeek")),
        fromDate: this.localizationService.translateGeneral("datepicker.strings.fromDate"),
        toDate: this.localizationService.translateGeneral("datepicker.strings.toDate"),
        todayButton: this.localizationService.translateGeneral("datepicker.strings.todayButton"),
      },
    };
  }

  get dateRangeInputLocalization(): DateRangeInputLocalization {
    return {
      ...this.datePickerLocalization,
      from: this.translateGeneral("dateRangeInput.from"),
      to: this.translateGeneral("dateRangeInput.to"),
    };
  }

  dateRangeInputCustomLocalization(string: string): DateRangeInputLocalization {
    return {
      ...this.datePickerLocalization,
      from: this.translateGeneral(`${string}.from`),
      to: this.translateGeneral(`${string}.to`),
    };
  }

  get numberRangeInputLocalization(): NumberRangeInputLocalization {
    return {
      fromPlaceholder: this.translateGeneral("numberRangeInput.from"),
      toPlaceholder: this.translateGeneral("numberRangeInput.to"),
    };
  }
  numberRangeInputCustomLocalization(string: string): NumberRangeInputLocalization {
    return {
      fromPlaceholder: this.translateGeneral(`${string}.from`),
      toPlaceholder: this.translateGeneral(`${string}.to`),
    };
  }
}

const localizationProvider = new LocalizationProvider();
export default localizationProvider;
